// src/helpers/folio.js
import { doc, runTransaction, getDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../Firebase/firebaseconfig";

/* Util */
const pad = (n, width) => String(n).padStart(width, "0");

/* =========================
 *   Núcleo de series/folios
 * =========================
 * Doc de cada serie en: counters/<serie>
 *  { seq: number, prefix: string, pad: number }
 * Lock de unicidad: folio_locks/<serie>/items/<FOLIO>
 */

/** Lee la config actual de una serie, sin incrementarla. Si no existe, devuelve defaults. */
export async function getSeriesConfig(serie) {
  const ref = doc(db, "counters", serie);
  const snap = await getDoc(ref);
  if (!snap.exists()) return { seq: 0, prefix: serie === "deudas" ? "DE" : "PR", pad: 5 };
  const d = snap.data();
  return { seq: Number(d.seq || 0), prefix: d.prefix ?? "PR", pad: Number(d.pad ?? 5) };
}

/** Devuelve el próximo folio de la serie SIN incrementar (para previsualizar en UI). */
export async function previewNextFolio(serie) {
  const { seq, prefix, pad: padLen } = await getSeriesConfig(serie);
  const next = seq + 1;
  return `${prefix}-${pad(next, padLen)}`;
}

/** Incrementa atómicamente la serie y devuelve { folio, index, prefix, pad }. */
export async function nextFolio(serie, defaults = {}) {
  const ref = doc(db, "counters", serie);
  const { folio, index, prefix, pad: padLen } = await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    let prefix = defaults.prefix ?? (serie === "deudas" ? "DE" : "PR");
    let padLen = Number(defaults.pad ?? 5);
    let next;

    if (!snap.exists()) {
      next = 1;
      tx.set(ref, { seq: next, prefix, pad: padLen });
    } else {
      const d = snap.data();
      prefix = d.prefix ?? prefix;
      padLen = Number(d.pad ?? padLen);
      next = Number(d.seq || 0) + 1;
      tx.update(ref, { seq: next, prefix, pad: padLen });
    }
    return { folio: `${prefix}-${pad(next, padLen)}`, index: next, prefix, pad: padLen };
  });
  return { folio, index, prefix, pad: padLen };
}

/** Reserva N folios contiguos de la serie en una sola transacción. */
export async function reservarFolios(serie, n = 1, defaults = {}) {
  if (n <= 0) return { folios: [], start: 0, prefix: "", pad: 0 };
  const ref = doc(db, "counters", serie);
  const res = await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    let prefix = defaults.prefix ?? (serie === "deudas" ? "DE" : "PR");
    let padLen = Number(defaults.pad ?? 5);
    let start;

    if (!snap.exists()) {
      start = 1;
      tx.set(ref, { seq: n, prefix, pad: padLen });
    } else {
      const d = snap.data();
      prefix = d.prefix ?? prefix;
      padLen = Number(d.pad ?? padLen);
      start = Number(d.seq || 0) + 1;
      tx.update(ref, { seq: start + n - 1, prefix, pad: padLen });
    }
    const folios = Array.from({ length: n }, (_, i) => `${prefix}-${pad(start + i, padLen)}`);
    return { folios, start, prefix, pad: padLen };
  });
  return res;
}

/**
 * Candado anti-duplicados: úsalo DENTRO de la transacción donde creas el doc.
 * Si el lock existe, la transacción fallará y evitas folios repetidos.
 *
 *   await runTransaction(db, async (tx) => {
 *     const { folio } = await nextFolio("propiedades"); // fuera o dentro según tu flujo
 *     lockFolioTX(tx, "propiedades", folio, newDocId);
 *     tx.set(doc(db, "propiedades", newDocId), {..., folio});
 *   });
 */
export function lockFolioTX(tx, serie, folio, refId = null) {
  const lockRef = doc(db, "folio_locks", serie, "items", folio);
  tx.set(lockRef, { refId: refId ?? null, createdAt: serverTimestamp() });
}

/* =========================
 *     Wrappers prácticos
 * ========================= */
export async function nextFolioPropiedad() {
  const { folio } = await nextFolio("propiedades", { prefix: "PR", pad: 5 });
  return { folio };
}
export async function nextFolioDeuda() {
  const { folio } = await nextFolio("deudas", { prefix: "DE", pad: 5 });
  return { folio };
}
export async function reservarFoliosPropiedad(n = 1) {
  return reservarFolios("propiedades", n, { prefix: "PR", pad: 5 });
}
